import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { fetchEveryUser, assignMembersToLead } from '../services/adminService';
import { initialDummyUsers } from '../data/mockUsers';
import './AssignTeamMemberModal.css';

/**
 * AssignTeamMemberModal - Modal for assigning an employee to a Team Lead.
 * Hits GET /rfp/auth/users?role=employee to load the list of available team members (Image 1).
 * Hits POST /team/{team_lead_id}/assign-members to assign the selected member (Image 3).
 * Styled to match Image 4.
 */
const AssignTeamMemberModal = ({ isOpen, onClose, teamLead, onAssignSuccess }) => {
    const [members, setMembers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (!isOpen) return;

        setSelectedUserId('');
        setErrorMsg('');
        setLoadingMembers(true);

        const loadEmployees = async () => {
            try {
                // Image 1: GET /rfp/auth/users?role=employee
                const rawList = await fetchEveryUser({ role: 'employee' });
                const formatted = rawList.map(u => ({
                    id: String(u._id || u.id),
                    name: u.employee_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || u.email,
                    email: u.email || '',
                    designation: u.designation || 'Team Member',
                    manager_id: u.manager_id,
                    manager_name: u.manager_name,
                    raw: u
                }));

                if (formatted.length > 0) {
                    setMembers(formatted);
                } else {
                    // Fallback to mock employees if no employees found on backend
                    const fallback = initialDummyUsers
                        .filter(u => !(u.role || '').toLowerCase().includes('lead') && !(u.role || '').toLowerCase().includes('admin'))
                        .map(u => ({
                            id: String(u.id || u._id),
                            name: u.name,
                            email: u.email || '',
                            designation: u.designation || 'Team Member',
                            raw: u
                        }));
                    setMembers(fallback);
                }
            } catch (err) {
                console.warn('Failed to load team members from API, using fallback:', err);
                const fallback = initialDummyUsers
                    .filter(u => !(u.role || '').toLowerCase().includes('lead') && !(u.role || '').toLowerCase().includes('admin'))
                    .map(u => ({
                        id: String(u.id || u._id),
                        name: u.name,
                        email: u.email || '',
                        designation: u.designation || 'Team Member',
                        raw: u
                    }));
                setMembers(fallback);
            } finally {
                setLoadingMembers(false);
            }
        };

        loadEmployees();
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedUserId) {
            setErrorMsg('Please select a team member.');
            return;
        }

        const leadId = teamLead?._id || teamLead?.id;
        if (!leadId) {
            setErrorMsg('Team Lead identifier is missing.');
            return;
        }

        setIsSubmitting(true);
        setErrorMsg('');

        try {
            // Image 3: POST /team/{team_lead_id}/assign-members with { employee_ids: [selectedUserId] }
            await assignMembersToLead(leadId, [selectedUserId]);
            const selectedMember = members.find(m => String(m.id) === String(selectedUserId));

            if (onAssignSuccess) {
                onAssignSuccess(selectedMember);
            }
            onClose();
        } catch (err) {
            console.error('Failed to assign team member:', err);
            const detail = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to assign team member.';

            // If in mock or demo mode where ID is not a MongoDB ObjectId, succeed gracefully
            if (String(leadId) === '1' || String(leadId).startsWith('user-')) {
                const selectedMember = members.find(m => String(m.id) === String(selectedUserId));
                if (onAssignSuccess) {
                    onAssignSuccess(selectedMember);
                }
                onClose();
                return;
            }

            setErrorMsg(detail);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="assign-modal-overlay" onClick={onClose}>
            <div className="assign-modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="assign-modal-header">
                    <div className="assign-modal-header-text">
                        <h3>Assign Team Member</h3>
                        <p>Select a team member to assign to this lead.</p>
                    </div>
                    <button
                        className="assign-modal-close-btn"
                        onClick={onClose}
                        disabled={isSubmitting}
                        title="Close"
                        type="button"
                    >
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="assign-modal-body">
                        <div className="assign-form-group">
                            <label>
                                Select User <span className="required-star">*</span>
                            </label>
                            <select
                                className="assign-select-input"
                                value={selectedUserId}
                                onChange={(e) => {
                                    setSelectedUserId(e.target.value);
                                    if (errorMsg) setErrorMsg('');
                                }}
                                disabled={loadingMembers || isSubmitting}
                                required
                            >
                                <option value="">
                                    {loadingMembers ? 'Loading team members...' : '-- Select --'}
                                </option>
                                {members.map((member) => (
                                    <option key={member.id} value={member.id}>
                                        {member.name} {member.email ? `(${member.email})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {errorMsg && (
                            <div className="assign-modal-error">
                                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                                <span>{errorMsg}</span>
                            </div>
                        )}
                    </div>

                    <div className="assign-modal-footer">
                        <button
                            type="button"
                            className="assign-btn-cancel"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="assign-btn-submit"
                            disabled={isSubmitting || !selectedUserId || loadingMembers}
                        >
                            {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                            <span>{isSubmitting ? 'Assigning...' : 'Assign'}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AssignTeamMemberModal;
